import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { 
  Truck, Tag, Thermometer, 
  Sparkles, Droplets, FileText,
  ChevronLeft, X, Building2, ClipboardCheck, Siren, FolderOpen, Bot
} from 'lucide-react';

// Import HACCP section components
import { 
  MerceArrivoSection, 
  EtichetteConservazioneSection, 
  FrigoriferiSection,
  PuliziaSection,
  OliEsaustiSection
} from '../components/haccp';
import FornitoriSection from '../components/haccp/FornitoriSection';
import IspezioneSection from '../components/haccp/ispezione/IspezioneSection';
import RichiamiSection from '../components/haccp/richiami/RichiamiSection';
import DocumentiSection from '../components/haccp/documenti/DocumentiSection';
import CartellaIspettoreSection from '../components/haccp/ispettore/CartellaIspettoreSection';
import AssistenteSection from '../components/haccp/assistente/AssistenteSection';

const HACCPPage = ({ initialSection }) => {
  const [selectedSection, setSelectedSection] = useState(null);

  const sections = [
    {
      id: 'assistente',
      title: 'Assistente HACCP',
      description: 'Chiedi per scritto, a voce o con una foto',
      icon: Bot,
      gradient: 'from-emerald-600 to-teal-400',
      shadowColor: 'shadow-emerald-500/30',
      available: true
    },
    {
      id: 'ispezione',
      title: 'Ispezione simulata',
      description: 'Voto e cosa sistemare prima dell\'ASL',
      icon: ClipboardCheck,
      gradient: 'from-rose-600 to-orange-500',
      shadowColor: 'shadow-rose-500/30',
      available: true
    },
    {
      id: 'cartella_ispettore',
      title: 'Cartella ispettore',
      description: 'QR con documenti e registri sempre pronti',
      icon: FolderOpen,
      gradient: 'from-slate-800 to-slate-500',
      shadowColor: 'shadow-slate-500/30',
      available: true
    },
    {
      id: 'richiami',
      title: 'Richiami prodotti',
      description: 'Trova e blocca la merce richiamata',
      icon: Siren,
      gradient: 'from-red-600 to-red-400',
      shadowColor: 'shadow-red-500/30',
      available: true
    },
    {
      id: 'merce_arrivo',
      title: 'Merce in arrivo',
      description: 'Registrazione e controllo forniture',
      icon: Truck,
      gradient: 'from-blue-500 to-cyan-400',
      shadowColor: 'shadow-blue-500/30',
      available: true
    },
    {
      id: 'etichette_conservazione',
      title: 'Etichette conservazione',
      description: 'Gestione etichette alimenti conservati',
      icon: Tag,
      gradient: 'from-green-500 to-emerald-400',
      shadowColor: 'shadow-green-500/30',
      available: true
    },
    {
      id: 'etichette_vendita',
      title: 'Etichette vendita',
      description: 'Etichette prodotti vendita',
      icon: Tag,
      gradient: 'from-purple-500 to-violet-400',
      shadowColor: 'shadow-purple-500/30',
      available: false
    },
    {
      id: 'frigoriferi',
      title: 'Frigoriferi',
      description: 'Monitoraggio temperature',
      icon: Thermometer,
      gradient: 'from-cyan-500 to-teal-400',
      shadowColor: 'shadow-cyan-500/30',
      available: true
    },
    {
      id: 'pulizia',
      title: 'Pulizia e sanificazione',
      description: 'Piano e registro pulizie',
      icon: Sparkles,
      gradient: 'from-yellow-500 to-amber-400',
      shadowColor: 'shadow-yellow-500/30',
      available: true
    },
    {
      id: 'oli_esausti',
      title: 'Oli esausti',
      description: 'Gestione smaltimento oli',
      icon: Droplets,
      gradient: 'from-orange-500 to-red-400',
      shadowColor: 'shadow-orange-500/30',
      available: true
    },
    {
      id: 'fornitori',
      title: 'Registro Fornitori',
      description: 'Tracciabilità fornitori HACCP',
      icon: Building2,
      gradient: 'from-indigo-500 to-blue-400',
      shadowColor: 'shadow-indigo-500/30',
      available: true
    },
    {
      id: 'documenti',
      title: 'Documenti',
      description: 'Documenti, attestati e scadenze',
      icon: FileText,
      gradient: 'from-sky-600 to-blue-400',
      shadowColor: 'shadow-blue-500/30',
      available: true
    }
  ];

  // Apertura diretta di una sezione (deep-link da notifica)
  useEffect(() => {
    if (initialSection) {
      const s = sections.find((x) => x.id === initialSection && x.available);
      if (s) setSelectedSection(s);
    }
  }, [initialSection]);

  return (
    <div className="space-y-4 lg:space-y-6">
      {/* Header - Mobile Dark / Desktop Light */}
      <div className="lg:block">
        <h1 className="text-lg lg:text-2xl font-bold text-white lg:text-gray-900">Gestione HACCP</h1>
        <p className="text-xs lg:text-sm text-slate-400 lg:text-gray-500">Gestisci la sicurezza alimentare del tuo locale</p>
      </div>

      {/* Cards Grid - Mobile Dark Style */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-2 lg:gap-4">
        {sections.map((section) => (
          <div 
            key={section.id}
            data-testid={`haccp-section-${section.id}`}
            onClick={() => section.available && setSelectedSection(section)}
            className={`bg-black/40 backdrop-blur-md lg:bg-white/90 backdrop-blur-sm rounded-2xl p-3 lg:p-6 border border-slate-700/50 lg:border-gray-100 transition-all ${
              section.available 
                ? 'active:scale-95 cursor-pointer shadow-sm hover:shadow-md hover:border-slate-600 lg:hover:border-gray-200' 
                : 'opacity-40 cursor-not-allowed'
            }`}
          >
            <div className="flex flex-col items-center text-center">
              <div className={`w-11 h-11 lg:w-14 lg:h-14 rounded-2xl bg-gradient-to-br ${section.gradient} flex items-center justify-center mb-2 lg:mb-3 shadow-lg ${section.shadowColor}`}>
                <section.icon className="h-5 w-5 lg:h-7 lg:w-7 text-white" />
              </div>
              <h3 className="font-semibold text-white lg:text-gray-900 text-sm lg:text-base mb-0.5 lg:mb-1">{section.title}</h3>
              <p className="text-[10px] lg:text-xs text-slate-400 lg:text-gray-500 line-clamp-2">{section.description}</p>
              {!section.available && (
                <span className="mt-1.5 lg:mt-2 px-2 py-0.5 bg-slate-700 lg:bg-gray-100 text-slate-400 lg:text-gray-500 text-[10px] lg:text-xs rounded-full">
                  Prossimamente
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Section Detail Modal - FULLSCREEN su Mobile / Modal su Desktop.
          Portal in #root: fuori dal layout mobile, così l'intestazione della pagina non la copre su iPhone */}
      {selectedSection && createPortal(
        <div className="mobile-content-wrapper">
        <div data-testid="haccp-section-modal" className="fixed inset-0 z-50 overflow-y-auto overscroll-contain lg:overflow-hidden lg:bg-black/70 lg:flex lg:flex-col lg:items-center lg:justify-center">
          {/* Mobile Background Image - same as global background */}
          <div 
            className="lg:hidden fixed inset-0 bg-cover bg-center bg-no-repeat"
            style={{ 
              backgroundImage: 'url(/images/login-bg.png)',
              zIndex: 0,
            }}
          >
            <div className="absolute inset-0 bg-black/60" />
          </div>
          
          {/* Mobile: pagina a tutto schermo che scorre (le finestre interne non vengono tagliate su iOS) / Desktop: Modal */}
          <div className="relative z-10 w-full min-h-full flex flex-col lg:min-h-0 lg:flex-none lg:bg-white lg:rounded-2xl lg:max-w-4xl lg:max-h-[95vh] lg:overflow-hidden lg:shadow-2xl">
            {/* Header - fisso in alto mentre si scorre */}
            <div className="sticky top-0 z-20 lg:static p-4 border-b border-white/10 lg:border-gray-100 flex items-center justify-between bg-[#05070f]/95 backdrop-blur-md lg:backdrop-blur-none lg:bg-gradient-to-r lg:from-blue-50 lg:to-purple-50 flex-shrink-0" style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 12px)' }}>
              <div className="flex items-center gap-3">
                <button 
                  onClick={() => setSelectedSection(null)}
                  data-testid="haccp-back-btn"
                  className="p-2 bg-black/30 backdrop-blur-sm lg:bg-white/50 hover:bg-black/40 lg:hover:bg-white/70 rounded-xl text-white lg:text-gray-500"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${selectedSection.gradient} flex items-center justify-center shadow-lg ${selectedSection.shadowColor}`}>
                  <selectedSection.icon className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-base lg:text-lg font-bold text-white lg:text-blue-900">{selectedSection.title}</h2>
                  <p className="text-white/60 lg:text-blue-600 text-xs hidden sm:block">{selectedSection.description}</p>
                </div>
              </div>
              <button 
                onClick={() => setSelectedSection(null)}
                data-testid="haccp-close-btn"
                className="p-2 bg-black/30 backdrop-blur-sm lg:bg-white/50 hover:bg-black/40 lg:hover:bg-white/70 rounded-xl text-white lg:text-gray-500"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            {/* Content - Scrollabile, TRANSPARENT on mobile */}
            <div className="flex-1 bg-transparent lg:bg-white lg:overflow-y-auto" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)' }}>
              {selectedSection.id === 'etichette_conservazione' && <EtichetteConservazioneSection />}
              {selectedSection.id === 'merce_arrivo' && <MerceArrivoSection />}
              {selectedSection.id === 'frigoriferi' && <FrigoriferiSection />}
              {selectedSection.id === 'pulizia' && <PuliziaSection />}
              {selectedSection.id === 'oli_esausti' && <OliEsaustiSection />}
              {selectedSection.id === 'fornitori' && <FornitoriSection />}
              {selectedSection.id === 'ispezione' && <IspezioneSection />}
              {selectedSection.id === 'richiami' && <RichiamiSection />}
              {selectedSection.id === 'documenti' && <DocumentiSection />}
              {selectedSection.id === 'cartella_ispettore' && <CartellaIspettoreSection />}
              {selectedSection.id === 'assistente' && <AssistenteSection />}
            </div>
          </div>
        </div>
        </div>,
        document.getElementById('root') || document.body
      )}
    </div>
  );
};

export default HACCPPage;
